import React from 'react';
import { am5, am5percent, am5themes_Animated } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createSeries } from './methods/create-series';
import { createDownloadButton } from '../shared/create-download-button';

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  numberFormat: string;
  download: boolean;
  tooltipText: string;
  category: string;

  hideSeries: string[];
  doughnut: boolean;
  seriesOrder: (_c: string[]) => string[];
  onSelect: (_category: string) => void;
  hideLabelsOnActive: boolean;
  truncateLabels: number | undefined;
};

const Chart = React.forwardRef((props: ChartProps, ref) => {
  const {
    id,
    data,
    fullScreen,
    numberFormat,
    download,
    category,
    hideSeries,
    tooltipText,
    seriesOrder,
    doughnut,
    onSelect,
    hideLabelsOnActive,
    truncateLabels,
  } = props;

  const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;
  const chartRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    deselectActiveSlice: () => chartRef.current?.deselectActiveSlice(),
  }));

  React.useEffect(() => {
    // Create root element
    const root = am5.Root.new(widgetId);

    // Remove amCharts logo
    root._logo?.dispose();

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        layout: root.verticalLayout,
        innerRadius: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
      })
    );

    chartRef.current = chart;

    // Root settings
    chart.appear(1000, 100);
    root.numberFormatter.setAll({
      numberFormat,
    });

    // Create elements
    createSeries({
      root,
      chart,
      data,
      category,
      tooltipText,
      hideSeries,
      seriesOrder,
      doughnut,
      onSelect,
      hideLabelsOnActive,
      truncateLabels,
    });
    if (download) createDownloadButton(root, category, data);

    return () => {
      root.dispose();
    };
  }, [data]);

  return <div id={widgetId} style={{ width: '100%', height: '100%' }} />;
});

Chart.displayName = 'Chart';

export default Chart;
